import { Component, inject, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PartsRequestService } from '../../../services/parts-request.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { IssuePartsMode, IssuePartsWorkOrderData } from '../../../models/part-request.model';

@Component({
  selector: 'app-issue-parts-modal',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './issue-parts-modal.component.html',
  styleUrl: './issue-parts-modal.component.scss'
})
export class IssuePartsModalComponent implements OnDestroy {
  readonly IssuePartsMode = IssuePartsMode;
  @Input() mode: IssuePartsMode = IssuePartsMode.Single;
  @Input() workOrder: IssuePartsWorkOrderData | null = null;
  @Input() workOrders: IssuePartsWorkOrderData[] = [];

  isLoading = false;

  private modalInstance = inject(NgbActiveModal);
  private partsRequestService = inject(PartsRequestService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  ngOnDestroy(): void {}

  get title(): string {
    return this.transloco.translate(
      this.mode === IssuePartsMode.Bulk ? 'parts.modals.issueParts.bulkTitle' : 'parts.modals.issueParts.title'
    );
  }

  get subtitle(): string {
    return this.transloco.translate(
      this.mode === IssuePartsMode.Bulk ? 'parts.modals.issueParts.subtitleBulk' : 'parts.modals.issueParts.subtitle'
    );
  }

  get displayWorkOrders(): IssuePartsWorkOrderData[] {
    if (this.mode === IssuePartsMode.Single) {
      return this.workOrder ? [this.workOrder] : [];
    }
    return this.workOrders;
  }

  get hasOutOfStockParts(): boolean {
    return this.displayWorkOrders.some(wo => wo.parts.some(p => p.onHand < p.quantityRequested));
  }

  get canIssueParts(): boolean {
    return !this.hasOutOfStockParts && this.displayWorkOrders.length > 0;
  }

  async onIssueParts(): Promise<void> {
    if (!this.canIssueParts) return;

    this.isLoading = true;
    try {
      await this.partsRequestService.issuePartsAsync({
        workOrders: this.displayWorkOrders.map(wo => ({
          workOrderId: wo.workOrderId,
          partRequestIds: wo.parts.map(p => p.id)
        }))
      });
      this.toastr.success(this.transloco.translate('parts.modals.issueParts.toastIssued'));
      this.modalInstance.close(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }
}